import RecipeCard from '@/components/RecipeCard';
import type { Category, RecipeSummary } from '@shared/schemas';

interface Props {
  recipes: RecipeSummary[];
  categoriesById: Map<number, Category>;
  /** true when the recipe is favourited by the logged-in user */
  isFavourite: (id: number) => boolean;
  className?: string;
  /**
   * When set, this is a teaser (the home feed) and phone shows at most this
   * many recipes. Tablet/desktop round the same teaser size up to a full row
   * for their own column count (3 / 4) instead of truncating mid-row —
   * capped by however many recipes actually exist. Omit to show everything
   * (used for category sections, which already pass an exact-size list).
   */
  feedCap?: number;
  /**
   * The block at the top of the page: its first card is the LCP image, so it
   * loads eagerly at high priority (just that one: more high-priority requests
   * compete with the JS bundle the page needs to paint at all). Only in the
   * layout that is actually visible, since the other two are display:none but
   * would still download.
   */
  priority?: boolean;
}

type Breakpoint = 'phone' | 'tablet' | 'desktop';
// Read once per render: only the loading attribute depends on it, and a
// resize after load changes nothing about what was already fetched.
const visibleBreakpoint = (): Breakpoint =>
  window.matchMedia('(min-width: 1024px)').matches
    ? 'desktop'
    : window.matchMedia('(min-width: 640px)').matches
      ? 'tablet'
      : 'phone';


// The staggered card block used by both the home feed and each category
// section: on a phone the first three recipes form "one tall left + two stacked
// right", any remaining ones fall into plain two-up rows. Tablet and desktop
// get a uniform multi-column grid instead.
export default function RecipeMosaic({
  recipes,
  categoriesById,
  isFavourite,
  className,
  feedCap,
  priority = false,
}: Props) {
  if (recipes.length === 0) return null;
  const eager = priority ? visibleBreakpoint() : null;

  // Round the teaser size up to a full row for `cols` columns, never past
  // how many recipes there actually are.
  const capForCols = (cols: number) =>
    feedCap === undefined ? recipes.length : Math.min(recipes.length, Math.ceil(feedCap / cols) * cols);

  const phoneRecipes = recipes.slice(0, feedCap ?? recipes.length);
  const tabletRecipes = recipes.slice(0, capForCols(3));
  const desktopRecipes = recipes.slice(0, capForCols(4));

  const card = (r: RecipeSummary, sizeClass: string, layout: Breakpoint, index: number) => (
    <RecipeCard
      key={r.id}
      recipe={r}
      categoriesById={categoriesById}
      className={sizeClass}
      favourite={isFavourite(r.id)}
      priority={eager === layout && index === 0}
    />
  );

  return (
    <>
      {/* phone: fixed staggered layout (one tall left, two stacked right, then rows) */}
      <div className={`space-y-3 sm:hidden ${className ?? ''}`}>
        {phoneRecipes.length <= 2 ? (
          <div className="grid grid-cols-2 gap-3">{phoneRecipes.map((r, i) => card(r, 'h-44', 'phone', i))}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              {/* tall card stretches to match the two stacked cards' height */}
              {card(phoneRecipes[0]!, 'h-full min-h-[18rem]', 'phone', 0)}
              <div className="space-y-3">
                {card(phoneRecipes[1]!, 'h-36', 'phone', 1)}
                {card(phoneRecipes[2]!, 'h-36', 'phone', 2)}
              </div>
            </div>
            {phoneRecipes.length > 3 && (
              <div className="grid grid-cols-2 gap-3">
                {phoneRecipes.slice(3).map((r, i) => card(r, 'h-44', 'phone', i + 3))}
              </div>
            )}
          </>
        )}
      </div>

      {/* tablet: uniform 3-column grid, rounded up to a full row */}
      <div className={`hidden gap-3 sm:grid sm:grid-cols-3 lg:hidden ${className ?? ''}`}>
        {tabletRecipes.map((r, i) => card(r, 'h-48', 'tablet', i))}
      </div>

      {/* desktop: uniform 4-column grid, rounded up to a full row */}
      <div className={`hidden gap-3 lg:grid lg:grid-cols-4 ${className ?? ''}`}>
        {desktopRecipes.map((r, i) => card(r, 'h-48', 'desktop', i))}
      </div>
    </>
  );
}
